<template>
    <div class="form-group" :class="{'has-error': errors.has('invoiceId')}">
        <label for="invoiceId" class="col-sm-2 control-label">Invoices to follow up on:</label>
        <div v-if="!disabled" class="col-sm-5">
            <div class="select-dropdown">
                <select v-validate.disable="'required'" data-vv-as="invoices" class="form-control" id="invoiceId"
                        name="invoiceId" v-model="selectedInvoiceIds" multiple :class="{'invalid-state': selectedInvoiceIds.includes(noInvoicesMessage)}"
                        @change="changeInvoice" :disabled="timeEntry.isAuditEdit">
                    <template v-if="this.timeEntry.sendEmail === 'Yes' && timeEntry.attachInvoice === 'Yes'">
                        <template v-for="invoice in unpaidInvoices">
                            <option :value="invoice.invoiceId" :disabled="invoice.Error" @mousedown="handleOptionClick(invoice.invoiceId)">
                                {{invoice.invoiceId}} - {{ invoice.isSubscription ? invoice.companyName + ' - ' : '' }}{{ invoice.overdueDays }} days
                            </option>
                        </template>
                    </template>
                    <template v-else>
                        <option v-for="invoice in unpaidInvoices" :value="invoice.invoiceId" @mousedown="handleOptionClick(invoice.invoiceId)">
                            {{invoice.invoiceId}} - {{ invoice.isSubscription ? invoice.companyName + ' - ' : '' }}{{ invoice.overdueDays }} days
                        </option>
                    </template>
                </select>
            </div>
            <p class="text-danger" v-if="errors.has('invoiceId')">{{ errors.first('invoiceId') }}</p>

            <!-- Display selected invoices -->
            <div v-if="selectedInvoiceIds && selectedInvoiceIds.length > 0" class="selected-invoices">
                <small class="text-muted">Selected invoices: {{ selectedInvoiceIds.join(', ') }}</small>
                <div v-if="subscriberList && subscriberList.length > 1">
                    <div v-if="this.timeEntry.sendEmail === 'Yes'">
                        <small class="text-danger">You selected invoices from multiple subscribers. Email will be sent to the last selected subscriber</small>
                    </div>
                    <div v-else>
                        <small class="text-warning">You have selected invoices from multiple subscribers</small>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="col-sm-6">
            <p>{{ timeEntry.invoiceId }}</p>
        </div>
    </div>
</template>

<script>
    import Vue from "vue";
    import axios from 'axios';
    import { MessageBox } from 'uiv';

    export default {
        inject: ['$validator'],
        data: function () {
            return {
                timeEntry: this.$store.state.timeEntry,
                unpaidInvoices: [],
                selectedInvoiceIds: [],
                noInvoicesMessage: "",
                subscriberList: [],
                lastClickedInvoiceId: null
            };
        },
        props: {
            isOutlook: Boolean,
            disabled: Boolean
        },
        // fetches list of unpaid invoices for the claim.
        created() {
            var authHeader = "";
            let self = this;
            let branchId = this.$store.state.branchId;
            let fileId = this.$store.state.fileId;
            let unpaidInvoiceUrl = `/core/api/claims/${branchId}/${fileId}/invoices/unpaid`;
            if (this.isOutlook) {
                unpaidInvoiceUrl = this.$store.state.keaUrl + unpaidInvoiceUrl;
                authHeader = { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                // Turn off send email flag if in Outlook plugin
                this.timeEntry.sendEmail = 'No';
            }
            this.noInvoicesMessage = "No unpaid invoices found";

            axios.get(unpaidInvoiceUrl, authHeader)
                .then(response => {
                    self.unpaidInvoices = response.data;

                    if (self.unpaidInvoices.length === 0) {
                        self.unpaidInvoices.push({
                            "invoiceId": self.noInvoicesMessage,
                            "exists": true,
                            "error": true
                        });
                        self.selectedInvoiceIds = [self.noInvoicesMessage];
                    } else {
                        // Initialize with the first invoice if there was a previously selected single invoice
                        if (self.timeEntry.invoiceId) {
                            self.selectedInvoiceIds = [self.timeEntry.invoiceId];
                            self.subscriberList = [self.timeEntry.companyName]
                        }
                    }
                });
        },
        methods: {
            handleOptionClick(invoiceId) {
                this.lastClickedInvoiceId = invoiceId;
            },
            changeInvoice() {
                let self = this;
                // Remove exisitng error messgae for email when selction changes
                this.errors.remove('emailTo');
                this.errors.remove('adhocEmail');
                // Handle multiple selected invoices
                let nonExistentInvoices = [];
                this.subscriberList = [];

                // Check each selected invoice
                this.selectedInvoiceIds.forEach(selectedInvoiceId => {
                    let invoice = _.find(this.unpaidInvoices, ['invoiceId', selectedInvoiceId]);
                    if (invoice && !invoice.exists && this.timeEntry.sendEmail === 'Yes') {
                        nonExistentInvoices.push(selectedInvoiceId);
                    }
                    // Keep track of unique subscribers
                    if (!this.subscriberList.includes(invoice.companyName)) {
                        this.subscriberList.push(invoice.companyName);
                    }
                });

                // If there are non-existent invoices, show modal for PDF generation
                if (nonExistentInvoices.length > 0) {
                    let invoiceList = nonExistentInvoices.join(', ');
                    MessageBox.alert({
                        html: true,
                        size: "md",
                        title: 'Generating Invoice(s)',
                        content: `<h2>Invoices ${invoiceList} have not been generated</h2><p>New tabs will be opened to generate PDF copies of the invoices. After the invoices are generated, close the tabs and return to this page.</p>`
                    }, (msg) => {
                        // callback after modal dismissed
                        nonExistentInvoices.forEach(invoiceId => {
                            self.generateInvoice(invoiceId);
                        });
                    });
                }
                else if (this.isOutlook) {
                    // For Outlook add the overdue invoices phrase ahead of content
                    let note = self.timeEntry.noteText;
                    let invoiceData = "Overdue KA Invoices#: " + this.selectedInvoiceIds.join(', ') +
                        "; KA File#: " + self.$store.state.timeEntry.fileId +
                        "; Insured: " + self.$store.state.timeEntry.insuredCompany + "\n";
                    if (note.indexOf("Overdue KA Invoice") === 0) {
                        note = note.replace(note.substring(0, note.indexOf("\n") + 1), invoiceData);
                    }
                    else {
                        note = invoiceData + note;
                    }
                    self.timeEntry.noteText = note;
                }

                // Update the store with the first selected invoice for backward compatibility
                if (this.selectedInvoiceIds.length > 0 && this.selectedInvoiceIds[0] !== this.noInvoicesMessage) {
                    this.timeEntry.invoiceId = this.lastClickedInvoiceId;
                } else {
                    this.timeEntry.invoiceId = null;
                }
                this.timeEntry.selectedInvoiceIds = this.selectedInvoiceIds.join(',');
                self.timeEntry.isMultiInvoice = this.selectedInvoiceIds.length > 1;
                self.timeEntry.isSameSubscriberInvoices = this.subscriberList.length == 1;

                this.$emit('change');
            },
            generateInvoice(invoiceId) {
                window.open(`/core/sso/OpenKeaView?KVDestPage=UI/Reports/ReportViewer.aspx?Report=101%26@strInvoiceList=${invoiceId}%26IsDuplicate=TRUE%26FileName=${invoiceId}%26ExportType=PDF`, '_blank');
                // mutate the unpaidInvoices array to record the fact that the invoice now exists
                const invoice = this.unpaidInvoices.find(invoice => invoice.invoiceId === invoiceId);
                if (invoice) {
                    invoice.exists = true;
                }
            }
        }
    };
</script>

<style scoped>
    .selected-invoices {
        margin-top: 5px;
    }

    /* Style the multi-select dropdown */
    select[multiple] {
        height: auto;
        min-height: 100px;
        width: auto;
    }

        select[multiple] option {
            padding: 5px;
        }

            select[multiple] option:checked {
                background-color: #007bff;
                color: white;
            }
</style>